/**
 * TOOL_SPEC loader and normalizer.
 * Discovers, parses, and normalizes TOOL_SPEC.json files into canonical internal representation.
 * Node-only fs/path usage lives in specLoaderNode.ts so the browser bundle never loads them.
 */

import { normalizeToolCategory } from "./category";
import type {
  CanonicalToolCategory,
  ToolFlags,
  ToolConfigField,
  HarmonizedToolAdapter,
  CriterionType,
} from "./types";
import { getBundledSpecs } from "./bundledSpecs";

// ============================================================================
// Section E: ToolRequirements Schema (Batch 3a)
// ============================================================================

/**
 * Prerequisite codes per Section B.
 * These are workflow artifacts that must be true for tool availability.
 */
export type PrerequisiteCode =
  | 'CRITERIA_SELECTED'    // ≥1 criterion chosen for the relevant scope/session
  | 'CRITERIA_LOCKED'      // criteria set is locked (immutable for this scope/version)
  | 'SCALES_DEFINED'       // every criterion has a defined scale
  | 'SCALES_ACCEPTED'      // criteria scales accepted/ratified for use
  | 'POOL_EXISTS'          // a candidate pool exists for the scope/session
  | 'POOL_NONEMPTY'        // pool has ≥ N items
  | 'POOL_LOCKED'          // pool is locked (stable membership)
  | 'VALUES_SUFFICIENT'    // required values exist for required criteria across pool
  | 'VALUES_CONVERGED'     // disputed/unknown values resolved per policy
  | 'ALTITUDE_SET';        // altitude defined for candidates when tool requires it

// CriterionType imported from ./types (Section A semantic types)

/**
 * Scale types per Section A.
 * Describes the value domain a criterion uses.
 */
export type ScaleType =
  | 'numeric_range'  // min/max range
  | 'fibonacci'      // estimation scale
  | 'tshirt'         // T-shirt sizes (XS<S<M<L<XL)
  | 'likert_1_5'     // 5-point Likert
  | 'likert_1_7'     // 7-point Likert
  | 'state_enum'     // categorical states
  | 'toggle';        // boolean toggle

/**
 * Required criterion specification per Section E.
 * Defines what criteria must exist for a tool to be available.
 */
export interface RequiredCriterion {
  /** Tag pattern for the criterion (e.g., "axis:x", "axis:y", "term:reach", "filter:*") */
  tag: string;
  /** Allowed criterion types */
  criterionTypes: CriterionType[];
  /** Optional: allowed scale types for more specific validation */
  scaleTypes?: ScaleType[];
  /** Minimum number of criteria matching this spec (default: 1) */
  minCount?: number;
}

/**
 * Pool requirements per Section E.
 * Defines constraints on the candidate pool for tool availability.
 */
export interface PoolRequirements {
  /** Minimum number of items in pool */
  minItems?: number;
  /** Whether pool must be locked for this tool */
  requireLocked?: boolean;
}

/**
 * Tool Requirements schema per Section E.
 * This is the canonical shape for expressing what a tool needs.
 */
export interface ToolRequirements {
  /** Section B prerequisite codes (workflow truth) */
  prerequisites: PrerequisiteCode[];
  /** Required criteria with tag and type specifications */
  requiredCriteria?: RequiredCriterion[];
  /** Pool size and lock requirements */
  poolRequirements?: PoolRequirements;
}

/**
 * Canonical criterion tag patterns per Section C.
 * Used to standardize how tools reference criteria.
 */
export const CRITERION_TAG_PATTERNS = {
  /** X-axis criterion for 2x2 grids (Impact-Effort) */
  AXIS_X: 'axis:x',
  /** Y-axis criterion for 2x2 grids (Impact-Effort) */
  AXIS_Y: 'axis:y',
  /** Formula term prefix (RICE, WSJF, custom formulas) */
  TERM_PREFIX: 'term:',
  /** Eligibility filter prefix */
  FILTER_PREFIX: 'filter:',
  /** Rubric dimension prefix (calibration, ranking) */
  RUBRIC_PREFIX: 'rubric:',
  /** Category placement prefix (pyramid) */
  CATEGORY_PREFIX: 'category:',
} as const;

// ============================================================================
// Raw Tool Spec Interface
// ============================================================================

/** Shared value-domain constraints for an item attribute (required or optional). */
interface AttributeConstraints {
  min?: number;
  max?: number;
  allowed_values?: any[];
}

/**
 * Algorithm-level priority/ordering scheme referenced by `tie_breaking.primary`
 * for category- or quadrant-based tools (e.g. Eisenhower Matrix, MoSCoW).
 * Different tools key the ranked entries by `category`, `quadrant`, or a
 * `condition` expression, so all three are optional.
 */
type AlgorithmPriorityScheme = Array<{
  quadrant?: string;
  category?: string;
  condition?: string;
  rank?: number;
  description: string;
}>;

/**
 * Raw TOOL_SPEC structure matching the integration contract.
 * This mirrors the JSON schema from TOOL_INTEGRATION_CONTRACT.md.
 *
 * Several fields below are wider than the contract's baseline shape because
 * individual TOOL_SPEC.json files legitimately carry tool-specific metadata
 * (e.g. a quaternary tie-breaker, a category hierarchy, participant-flow
 * flags) that the original interface didn't yet account for. None of this
 * extra metadata is consumed by `normalizeSpec` below — it flows through via
 * `raw` for documentation/UI use — so widening here doesn't change behavior.
 */
export interface RawToolSpec {
  tool_spec_version: string;
  identity: {
    tool_name: string;
    short_label: string;
    description: string;
    category: string;
    status: string;
    /** Provenance/citation metadata for tools based on a published framework. */
    attribution?: {
      primary_source?: string;
      secondary_source?: string;
      source_urls?: string[];
    };
  };
  semantics: {
    purpose: string;
    requires_comparable_items: boolean;
    notes_on_comparability?: string;
  };
  inputs: {
    required_item_fields: string[];
    required_item_attributes: Array<{
      key: string;
      type: string;
      description: string;
      constraints?: AttributeConstraints;
      /** Some specs place allowed_values at the top level instead of nesting under constraints. */
      allowed_values?: any[];
    }>;
    optional_item_attributes: Array<{
      key: string;
      type: string;
      description: string;
      default?: any;
      constraints?: AttributeConstraints;
    }>;
    criteria_set_contract: {
      allowed_criteria_ids: string[] | "dynamic" | "any" | null;
      requires_explicit_criteria_set: boolean;
      notes?: string;
    };
    lens_support: {
      requires_lens: boolean;
      notes?: string;
    };
    tool_config: {
      fields: Array<{
        name: string;
        type: string;
        allowed_values?: any[] | null;
        default?: any;
        min?: number;
        max?: number;
        description?: string;
        required?: boolean;
      }>;
      supports_defaults: boolean;
      notes?: string;
    };
  };
  algorithm: {
    score_model_type: string;
    formula_description?: string;
    formula_variables?: Record<string, string>;
    scaling_and_normalization?: Record<string, string | boolean | null>;
    handles_missing_values?: {
      strategy: string;
      rules?: string[];
    };
    tie_breaking?: {
      primary?: string;
      secondary?: string;
      tertiary?: string;
      quaternary?: string;
      notes?: string;
      optional_tie_breaker_round?: {
        enabled: boolean;
        description: string;
        algorithm: string;
      };
    };
    /** Ordering scheme the tie_breaking primary rule refers to by name. */
    category_hierarchy?: AlgorithmPriorityScheme;
    quadrant_priority?: AlgorithmPriorityScheme;
    quadrant_rules?: AlgorithmPriorityScheme;
    determinism?: boolean;
    notes?: string;
    /** Free-form algorithm-detail blocks that vary per tool (e.g. AHP's eigenvector weight derivation, pairwise ranking's comparison-generation strategy). Descriptive only. */
    weight_derivation?: Record<string, unknown>;
    comparison_generation?: Record<string, unknown>;
    consistency_validation?: Record<string, unknown>;
    scoring_process?: Record<string, unknown>;
  };
  outputs: {
    score_set_schema: {
      per_item_fields: Array<{
        name: string;
        type: string;
        description?: string;
        allowed_values?: any[] | null;
      }>;
      annotations?: Array<{
        name: string;
        type: string;
        description?: string;
        value?: any;
        allowed_values?: any[] | null;
      }>;
    };
    tool_run_metadata: {
      records_raw_inputs: boolean;
      raw_inputs_schema?: Record<string, string | Record<string, unknown>>;
      additional_fields?:
        | Record<string, string | Record<string, string>>
        | Array<{ name: string; type: string; description?: string }>;
      additional_metadata?: string[] | Record<string, string>;
      /** Present for tools (e.g. AHP) whose run metadata includes consistency-ratio or sensitivity diagnostics. */
      includes_consistency_metrics?: boolean;
      includes_sensitivity_analysis?: boolean;
      notes?: string;
    };
  };
  interaction_model: {
    ui_pattern: string;
    supports_inline_edit: boolean;
    supports_batch_edit: boolean;
    step_flow: string[] | Array<{ phase: string; steps: string[]; validation?: string }>;
    validation_behavior?: {
      on_invalid_item?: string;
      on_any_error?: string;
      validation_rules?: string[];
      on_inconsistent_comparisons?: string;
      on_weight_sum_error?: string;
      on_missing_scores?: string;
      notes?: string;
    };
    ui_notes?: string;
    notes?: string;
    /**
     * Free-form UI-pattern metadata blocks that vary per tool (button
     * labels, keyboard shortcuts, turn-taking flags, input affordances).
     * Descriptive only — not consumed by normalizeSpec.
     */
    comparison_interface?: Record<string, unknown>;
    participant_flow?: Record<string, unknown>;
    real_time_feedback?: Record<string, unknown>;
    input_methods?: Record<string, unknown>;
    ui_characteristics?: Record<string, unknown>;
    interaction_notes?: string;
    workflow_notes?: string[];
  };
  constraints_and_assumptions: {
    max_items_tested?: number;
    performance_notes?: string;
    assumes_attribute_types?: string;
    assumptions?: string[];
    notes?: string;
    assumes_participant_model?: string;
    max_criteria?: number;
    max_items_recommended?: number;
    max_participants?: number;
    platform_dependencies?: string[];
    recommended_max_items?: number;
    visual_constraints?: string;
    assumes_platform_validation?: string;
    min_criteria?: number;
    scalability_notes?: string;
    warning_threshold?: number;
    assumes_user_consistency?: string;
    mathematical_assumptions?: string[];
    pseudo_quantitative_note?: string;
    computational_complexity?: Record<string, unknown>;
  };
  /**
   * Tool requirements per Section E ToolRequirements schema (Batch 3a).
   * Defines prerequisites, required criteria with tags, and pool requirements.
   * See Doc 01 §9, Doc 04 for artifact definitions.
   */
  toolRequirements?: ToolRequirements;
  /** UI-facing assumptions, distinct from constraints_and_assumptions' algorithmic ones. */
  ui_assumptions?: string[];
  /** Citation/methodology metadata for tools based on a published framework. */
  references?: Record<string, unknown>;
  test_vectors: Array<{
    name: string;
    items: Array<{
      id: string;
      /** Optional: one tool's test vectors use `title` instead (see runTestVector's fallback in testHarness.ts). */
      label?: string;
      attributes?: Record<string, any>;
      description?: string;
      quadrant?: string;
      scores?: Record<string, number>;
      type?: string;
      value?: any;
      impact?: number;
      rank?: number;
      title?: string;
      feasibility?: number;
      parentId?: string | null;
      status?: string;
    }>;
    tool_config?: Record<string, any>;
    criteria_set?: any;
    expected_scores?: Array<{
      item_id: string;
      score?: number;
      rank?: number;
      category?: string;
      average_rank?: number;
      calculation?: string;
      cd3_category?: string;
      final_score?: number;
      purchases?: number;
      segment_key?: string;
      value_lost?: number;
      votes?: number;
      weighted_total?: number;
      wins?: number;
      consensus_score?: number;
      cost_to_restart?: number;
      criterion_contributions?: Record<string, unknown>;
      efficiency_score?: number;
      losses?: number;
      timeHorizon?: string;
      total_spent?: number;
      vote_percentage?: number;
      comparisons?: number;
      is_tied?: boolean;
      strategicDirection?: string;
      tie_break_reason?: string;
      rank_within_segment?: number;
    }>;
    notes?: string;
    /** Free-form fixture context some tools' test vectors carry (e.g. a decision brief for a 2x2 tool). */
    decision_context?: Record<string, unknown>;
    excluded_items?: Array<Record<string, unknown>>;
    expected_categorizations?: Array<Record<string, unknown>>;
    expected_results?: Array<Record<string, unknown>>;
    goal?: string;
    description?: string;
    criteria?: unknown[];
    expected_result?: Record<string, unknown>;
    participant_rankings?: unknown[];
    expected_output?: Array<Record<string, unknown>>;
    item_scores?: Record<string, unknown>;
    pairwise_comparisons?: Array<Record<string, unknown>>;
    participant_actions?: Array<Record<string, unknown>>;
    expected_metadata?: Record<string, unknown>;
    expected_weights?: Record<string, unknown>;
    matchup_history?: Array<Record<string, unknown>>;
    votes?: Record<string, unknown>;
    expected_consistency_ratio?: number;
    expected_structure?: Record<string, unknown>;
    tie_breaker_history?: Array<Record<string, unknown>>;
  }>;
}

/**
 * Normalized tool specification with derived metadata.
 */
export interface NormalizedToolSpec {
  raw: RawToolSpec;
  toolName: string;
  category: CanonicalToolCategory;
  flags: ToolFlags;
  inputContract: HarmonizedToolAdapter["inputContract"];
  interactionModel: HarmonizedToolAdapter["interactionModel"];
}

/**
 * Validates that a parsed JSON object has the required top-level fields.
 */
function validateRawSpec(obj: any): obj is RawToolSpec {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof obj.tool_spec_version === "string" &&
    typeof obj.identity === "object" &&
    typeof obj.identity.tool_name === "string" &&
    typeof obj.semantics === "object" &&
    typeof obj.inputs === "object" &&
    typeof obj.algorithm === "object" &&
    typeof obj.outputs === "object" &&
    typeof obj.interaction_model === "object" &&
    typeof obj.constraints_and_assumptions === "object" &&
    Array.isArray(obj.test_vectors)
  );
}

/**
 * Normalizes a raw TOOL_SPEC into the internal representation.
 */
function normalizeSpec(raw: RawToolSpec): NormalizedToolSpec {
  const toolName = raw.identity.tool_name;
  const { category, flags } = normalizeToolCategory(
    raw.identity.category,
    toolName
  );

  // Extract required attributes
  const requiredAttributes = raw.inputs.required_item_attributes.map(
    (attr) => attr.key
  );
  const optionalAttributes = raw.inputs.optional_item_attributes.map(
    (attr) => attr.key
  );

  // Normalize criteria set contract. "dynamic" (runtime-determined), "any"
  // (unrestricted), and null (tool doesn't use scoring criteria at all, e.g. a
  // visual mapping tool) all mean "no fixed criteria ID list" — treat them
  // alike so the narrowed type is a plain string[].
  const rawAllowedIds = raw.inputs.criteria_set_contract.allowed_criteria_ids;
  const allowedCriteriaIds =
    rawAllowedIds === "dynamic" || rawAllowedIds === "any" || rawAllowedIds === null
      ? []
      : rawAllowedIds;

  // Normalize tool config fields
  const toolConfigFields: ToolConfigField[] = raw.inputs.tool_config.fields.map(
    (field) => ({
      name: field.name,
      type: field.type as ToolConfigField["type"],
      allowedValues: field.allowed_values ?? null,
      default: field.default,
      min: field.min,
      max: field.max,
      description: field.description,
    })
  );

  // Normalize step flow (handle both string[] and object[] formats)
  const stepFlow: string[] = [];
  if (Array.isArray(raw.interaction_model.step_flow)) {
    for (const step of raw.interaction_model.step_flow) {
      if (typeof step === "string") {
        stepFlow.push(step);
      } else if (typeof step === "object" && step !== null) {
        // Handle phase-based step flow
        if ("phase" in step && "steps" in step) {
          const phaseStep = step as { phase: string; steps: string[] };
          stepFlow.push(`${phaseStep.phase}: ${phaseStep.steps.join(", ")}`);
        }
      }
    }
  }

  return {
    raw,
    toolName,
    category,
    flags,
    inputContract: {
      requiredItemFields: raw.inputs.required_item_fields,
      requiredAttributes,
      optionalAttributes,
      criteriaSetContract: {
        allowedCriteriaIds,
        requiresExplicitCriteriaSet:
          raw.inputs.criteria_set_contract.requires_explicit_criteria_set,
      },
      lensRequired: raw.inputs.lens_support.requires_lens,
      toolConfigFields,
    },
    interactionModel: {
      uiPattern: raw.interaction_model.ui_pattern,
      stepFlow,
      supportsInlineEdit: raw.interaction_model.supports_inline_edit,
      supportsBatchEdit: raw.interaction_model.supports_batch_edit,
    },
  };
}


/**
 * Loads and normalizes all TOOL_SPEC.json files (synchronous).
 *
 * In browser/app mode: uses pre-bundled ALL_TOOL_SPECS from generated/toolSpecs.ts.
 * In Node mode: returns [] — use loadAllToolSpecsAsync() for filesystem discovery.
 *
 * @param _rootDir - Reserved for signature compatibility; filesystem discovery
 *   lives in loadAllToolSpecsAsync(). Ignored here.
 * @returns Array of normalized tool specifications (empty if none are bundled).
 */
export function loadAllToolSpecs(_rootDir?: string): NormalizedToolSpec[] {
  const isBrowser = typeof window !== "undefined";
  const isNode = typeof process !== "undefined" && process.versions?.node;

  if (isBrowser) {
    try {
      const bundled = getBundledSpecs();
      if (bundled && bundled.length > 0) {
        const normalizedSpecs: NormalizedToolSpec[] = [];
        for (const raw of bundled) {
          if (validateRawSpec(raw)) {
            normalizedSpecs.push(normalizeSpec(raw));
          }
        }
        return normalizedSpecs;
      }
      console.warn(
        "No pre-bundled tool specs found. Run 'npm run build:tool-specs' before building the app."
      );
      return [];
    } catch (error) {
      console.warn("Could not load pre-bundled tool specs:", error);
      return [];
    }
  }

  if (!isNode) {
    console.warn(
      "Cannot use Node.js fs APIs. Tool specs should be pre-bundled for browser use."
    );
    return [];
  }

  // Node without a browser bundle: filesystem discovery is inherently async and
  // must not pull node:fs/node:path into the browser bundle, so it lives in
  // loadAllToolSpecsAsync(). The synchronous contract returns [] here rather than
  // a Promise that no caller awaits.
  return [];
}

/**
 * Async Node-only variant of loadAllToolSpecs: discovers TOOL_SPEC.json files on
 * the filesystem via specLoaderNode. The dynamic import is code-split so browser
 * bundles never pull in node:fs/node:path. Use from Node scripts, SSR, or tests
 * that need live filesystem discovery; the browser app uses the synchronous
 * loadAllToolSpecs() against the pre-bundled specs.
 *
 * @param rootDir - Root directory to search for TOOL_SPEC files (defaults to cwd).
 * @returns Promise of normalized tool specifications.
 */
export async function loadAllToolSpecsAsync(rootDir?: string): Promise<NormalizedToolSpec[]> {
  const isNode = typeof process !== "undefined" && process.versions?.node;
  if (!isNode) {
    return loadAllToolSpecs();
  }
  const node = await import("./specLoaderNode");
  const searchRoot = rootDir || process.cwd();
  const specFiles = node.findToolSpecFiles(searchRoot);
  const raws = node.loadRawSpecsFromPaths(specFiles);
  const normalizedSpecs: NormalizedToolSpec[] = [];
  for (const parsed of raws) {
    if (!validateRawSpec(parsed)) continue;
    normalizedSpecs.push(normalizeSpec(parsed));
  }
  return normalizedSpecs;
}

